/**
 * Convert hex color to RGB
 */
export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Create gradient with varying shades of a single color
 */
export const createShadeGradient = (baseColor) => {
  const rgb = hexToRgb(baseColor);
  if (!rgb) return baseColor;
  
  // Create lighter and darker shades
  const lighter = `rgb(${Math.min(255, rgb.r + 40)}, ${Math.min(255, rgb.g + 40)}, ${Math.min(255, rgb.b + 40)})`;
  const base = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
  const darker = `rgb(${Math.max(0, rgb.r - 40)}, ${Math.max(0, rgb.g - 40)}, ${Math.max(0, rgb.b - 40)})`;
  
  return `linear-gradient(135deg, ${lighter}, ${base}, ${darker}, ${base}, ${lighter})`;
};

/**
 * Get avatar border styles based on user's profile group
 * @param {Object} user - User object with profile_group_color and is_holographic_group
 * @param {number} borderWidth - Border width in pixels (default: 5)
 * @returns {Object} Style object for the avatar
 */
export const getAvatarBorderStyle = (user, borderWidth = 2) => {
  if (!user?.profile_group_color) {
    return {
      border: `${borderWidth}px solid #ddd`,
      boxShadow: 'none'
    };
  }
  
  // Ensure is_holographic_group is a boolean
  const isHolographic = user.is_holographic_group === true;
  
  if (isHolographic) {
    // Holographic effect with single color shifting between shades
    const baseColor = user.profile_group_color;
    const rgb = hexToRgb(baseColor);
    
    const styles = {
      wrapper: {
        padding: `${borderWidth}px`,
        background: createShadeGradient(baseColor),
        backgroundSize: '200% 200%',
        backgroundPosition: '0% 50%', // Explicitly set initial position to match animation start
        backgroundRepeat: 'no-repeat', // Prevent tiling
        borderRadius: '50%',
        display: 'inline-block',
        position: 'relative', // Ensure proper positioning context
        animation: 'shift-shades 6s ease-in-out infinite',
        animationFillMode: 'both', // Ensure animation state is applied immediately
        animationDelay: '0s', // Explicitly set delay to ensure animation starts
        boxShadow: rgb
          ? `0 0 8px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.5), 0 0 16px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
          : `0 0 8px ${baseColor}80, 0 0 16px ${baseColor}4D`,
        boxSizing: 'border-box',
        overflow: 'hidden', // Ensure image doesn't overflow the border
        willChange: 'background-position', // Optimize for animation
        // Force background position to be applied immediately
        transform: 'translateZ(0)', // Force hardware acceleration and reflow
        backfaceVisibility: 'hidden', // Optimize rendering
        flexShrink: 0 // Prevent flex shrinking
      },
      image: {
        border: 'none',
        boxShadow: 'none',
        display: 'block',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        objectFit: 'cover'
      }
    };
    
    return styles;
  }

  // Standard colored border
  return {
    border: `${borderWidth}px solid ${user.profile_group_color}`,
    boxShadow: `0 0 15px ${user.profile_group_color}60`
  };
};

/**
 * Format numbered profile for display: pad numerator with zeros to match denominator digits.
 * e.g. "2/10" -> "02/10", "3/100" -> "03/100"
 * @param {string} value - Raw value like "2/10"
 * @returns {string} Formatted value or original if not "num/denom"
 */
export const formatNumberedProfile = (value) => {
  if (typeof value !== 'string' || !value) return value || '';
  const parts = value.split('/');
  if (parts.length !== 2) return value;
  const num = parts[0].trim();
  const denom = parts[1].trim();
  if (!denom.length || !/^\d+$/.test(num) || !/^\d+$/.test(denom)) return value;
  const padLength = denom.length;
  const paddedNum = num.padStart(padLength, '0');
  return `${paddedNum}/${denom}`;
};

/**
 * Get badge styles for profile number badges
 * @param {Object} user - User object with profile_group_color and is_holographic_group
 * @returns {Object} Style object for the badge
 */
export const getBadgeStyle = (user) => {
  if (!user?.profile_group_color) {
    return {};
  }

  if (user.is_holographic_group) {
    // Holographic effect with single color shifting between shades
    const baseColor = user.profile_group_color;
    const rgb = hexToRgb(baseColor);
    
    return {
      background: createShadeGradient(baseColor),
      backgroundSize: '200% 200%',
      animation: 'shift-shades 6s ease-in-out infinite',
      color: '#fff',
      textShadow: rgb 
        ? `0 0 4px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8), 0 0 8px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6)`
        : `0 0 4px ${baseColor}80, 0 0 8px ${baseColor}60`,
      boxShadow: rgb 
        ? `0 0 10px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6), 0 0 20px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.4)`
        : `0 0 10px ${baseColor}60, 0 0 20px ${baseColor}40`
    };
  }

  // Standard colored badge
  return {
    background: user.profile_group_color,
    color: '#fff',
    boxShadow: `0 0 8px ${user.profile_group_color}60`
  };
};

